/** What a candidate is told when the control plane refuses them.
 *
 * Its own module so the wording is a pure function with a test on it, rather
 * than a string buried in a component that only jsdom and a browser can reach.
 *
 * A candidate holds one credential and has no account to check, so every 4xx on
 * the way in is really a statement about their link. Only a 5xx or a failed
 * fetch is about the server, and the two must not share a sentence: "could not
 * reach the control plane" sends someone whose link expired to check their wifi.
 */

import { ApiError } from "./api";

/** What each refusal means to the person holding the link. */
export const REFUSED: Record<number, string> = {
  401: "This link has expired.",
  403: "This link is for a different interview session.",
  404: "This session no longer exists.",
};

/** Whether this failure is about the link rather than about reaching the server. */
export function isRefusal(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status >= 400 && err.status < 500;
}

/** The headline for a refusal. Anything unlisted is still the link's problem. */
export function refusalTitle(err: ApiError): string {
  return REFUSED[err.status] ?? "This link is no longer valid.";
}

/** One line for an inline error bar, said to the candidate rather than to
 * whoever wrote the server: `detail` is written for a staff member reading a
 * log, and "this session needs its own link" is not an instruction anybody
 * can follow. */
export function explain(err: unknown): string {
  if (isRefusal(err)) return `${refusalTitle(err)} Ask your interviewer for a new link.`;
  return err instanceof Error ? err.message : String(err);
}
